import React from "react";
import { Layout, Award, Smartphone, CalendarCheck, Sparkles } from "lucide-react";
import { handlePrimaryCta, PRIMARY_CTA_LABEL } from "@/lib/contact";

const PILLARS = [
  {
    icon: Layout,
    title: "No more repeating yourself",
    desc: "Every service has its own page — what it is, who it's for, what it costs, how to book. Clients arrive informed. You stop typing the same answer for the 100th time.",
  },
  {
    icon: Award,
    title: "Trust elements that turn lookers into bookers",
    desc: "Before/after, testimonials, credentials, real outcomes — placed exactly where high-intent buyers actually look. The result: better clients, fewer tire-kickers.",
  },
  {
    icon: Smartphone,
    title: "Looks premium on the only screen that matters",
    desc: "Most of your visitors are on a phone — whether they came from social, Google, an email, a flyer, or a friend's text. Your funnel is built mobile-first, loads instantly, and feels like the brands they already trust.",
  },
  {
    icon: CalendarCheck,
    title: "A booking flow, not a dead-end form",
    desc: "Visitors hit a structured path — choose service → see availability → book. Plays nicely with Acuity, Calendly, Square, Vagaro, GHL, or whatever you're already using.",
  },
];

export default function SolutionSection() {
  return (
    <section
      id="solution"
      data-testid="solution-section"
      className="section bg-nav-900/40 border-y border-nav-800"
    >
      <div className="container-x">
        <div className="max-w-3xl">
          <span className="eyebrow">
            <span className="h-1.5 w-1.5 rounded-full bg-brand" />
            The System
          </span>
          <h2 className="mt-4 h-display text-4xl sm:text-5xl">
            Not a website.{" "}
            <span className="text-brand">A booking funnel.</span>
          </h2>
          <p className="mt-4 text-lg text-nav-300">
            No more chasing leads. No more messy DM threads. No more "are you
            still interested?" Clients arrive ready to book — because the funnel
            already answered every question.
          </p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 gap-5">
          {PILLARS.map(({ icon: Icon, title, desc }, i) => (
            <div
              key={title}
              data-testid={`solution-pillar-${i}`}
              className="rounded-2xl border border-nav-800 bg-nav-900/70 p-6 hover:border-brand/40 transition-colors"
            >
              <div className="grid place-items-center h-11 w-11 rounded-xl bg-brand/15 border border-brand/20 text-brand">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display font-bold text-xl text-white">
                {title}
              </h3>
              <p className="mt-2 text-sm text-nav-300 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <a
            href="#contact"
            onClick={handlePrimaryCta}
            data-testid="solution-primary-cta"
            className="btn-primary text-lg"
          >
            <Sparkles className="h-5 w-5" />
            {PRIMARY_CTA_LABEL}
          </a>
        </div>
      </div>
    </section>
  );
}
